import { Toaster as Sonner, type ToasterProps } from "sonner"
import { CircleCheckIcon, InfoIcon, TriangleAlertIcon, OctagonXIcon, Loader2Icon } from "lucide-react"

/**
 * Toasts, in the page's colours rather than the operating system's.
 *
 * Two things made them unreadable, both reported from the 1 September demo as
 * "light grey text in some cases".
 *
 * The theme came from next-themes' `useTheme()`, and no ThemeProvider is
 * mounted anywhere, so it always resolved to "system" and sonner styled itself
 * from the OS preference. On a dark-mode machine the toast went dark while the
 * page stayed light. The `.dark` block in globals.css exists but nothing ever
 * applies the class, so the app is light and the toast should say so. When a
 * real theme toggle arrives, this is where it plugs back in.
 *
 * And only the `--normal-*` variables were set. Sonner reads a separate set
 * per variant, so every `toast.success` and `toast.error` — which is almost
 * all of them, around thirty call sites — fell back to sonner's own palette
 * instead of ours. Those are the "some cases".
 */
const Toaster = ({ ...props }: ToasterProps) => {
  return (
    <Sonner
      theme="light"
      className="toaster group"
      icons={{
        success: (
          <CircleCheckIcon className="size-4" />
        ),
        info: (
          <InfoIcon className="size-4" />
        ),
        warning: (
          <TriangleAlertIcon className="size-4" />
        ),
        error: (
          <OctagonXIcon className="size-4" />
        ),
        loading: (
          <Loader2Icon className="size-4 animate-spin" />
        ),
      }}
      style={
        {
          "--normal-bg": "var(--popover)",
          "--normal-text": "var(--popover-foreground)",
          "--normal-border": "var(--border)",
          "--border-radius": "var(--radius)",
          // Every variant, or the ones left out borrow sonner's palette.
          "--success-bg": "var(--popover)",
          "--success-text": "var(--popover-foreground)",
          "--success-border": "var(--border)",
          "--info-bg": "var(--popover)",
          "--info-text": "var(--popover-foreground)",
          "--info-border": "var(--border)",
          "--warning-bg": "var(--popover)",
          "--warning-text": "var(--popover-foreground)",
          "--warning-border": "var(--border)",
          "--error-bg": "var(--popover)",
          "--error-text": "var(--popover-foreground)",
          "--error-border": "var(--border)",
        } as React.CSSProperties
      }
      toastOptions={{
        classNames: {
          toast: "cn-toast",
        },
      }}
      {...props}
    />
  )
}

export { Toaster }
